import styled from 'styled-components';
import { HEIGHT_OF_COMPONENTS_OUTSIDE_IMPORT_TABLE_BODY } from '../../../consts/consts';
import { windowMaxWidth } from './breakpoints';

const IMPORT_TABLE_COLUMN_HEADER_HEIGHT = 41;
const IMPORT_COLUMNS_CHECKBOXES_HEIGHT = 45;
// For the empty state of the table normal overflow scrollbars are used, not the virtual list overflow scrollbars
// therefore the height of the empty state table body has to take into account the height of the 11px scrollbar
const HEIGHT_OF_COMPONENTS_OUTSIDE_IMPORT_TABLE_BODY_PLUS_SCROLLBAR =
  HEIGHT_OF_COMPONENTS_OUTSIDE_IMPORT_TABLE_BODY + 11;

const ImportMainWrapper = styled.div`
  align-items: center;
  background-color: var(--secondary-whisper);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
  width: 100%;

  .import-inner-wrapper {
    max-width: 1840px;
    width: 100%;
  }
`;

// flex-flow: row wrap lets the header gracefully wrap on splitScreen-width without changing
// the desktop look (items fit on one row at wide widths, wrap to the next line when they don't)
const ImportHeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-flow: row wrap;
  gap: 10px;
  justify-content: space-between;
  width: 100%;
`;

const ImportSectionWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-flow: row wrap;
  gap: 20px;
`;

const ImportHeaderButtonsWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 10px;
`;

const ImportUploaderSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const ImportLoadingStateWrapper = styled.div`
  background-color: var(--greyscale-white);
  border-radius: var(--border-radius-medium);
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const ImportUploaderDropzoneWrapper = styled.div`
  align-items: center;
  background-color: var(--greyscale-porcelain);
  border: 2px dashed var(--primary-oqio-blue);
  border-radius: 8px;
  display: flex;
  height: 100%;
  justify-content: center;
  padding: 0 20px;
  width: 100%;

  &:hover {
    cursor: pointer;
  }

  ${({ $hasError }) =>
    $hasError &&
    `
    border-color: var(--primary-oqio-red) !important;
    flex-direction: column;
    gap: 4px;
  `}
`;

const ImportUploaderTextWrapper = styled.div`
  display: flex;
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);

  @media ${windowMaxWidth.mobile} {
    flex-wrap: wrap;
    gap: 4px;
  }
`;

const ImportUploaderText = styled.span`
  color: var(--greyscale-blackish);

  ${({ $hasError }) => $hasError && `color: var(--primary-oqio-red);`}
`;

const ImportUploaderHighlightedText = styled.span`
  color: var(--primary-oqio-blue);
  font-weight: var(--font-weight-medium);
  margin-left: 4px;
  text-decoration: underline;

  @media ${windowMaxWidth.mobile} {
    margin-left: 0;
  }
`;

const ImportTableSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 100%;
  width: 100%;
`;

const ImportTableWrapper = styled.div`
  align-items: center;
  background-color: var(--greyscale-white);
  border: 1px solid var(--greyscale-dark-white);
  border-radius: var(--border-radius-medium);
  display: flex;
  height: calc(100% - ${IMPORT_COLUMNS_CHECKBOXES_HEIGHT}px);
  justify-content: center;
  min-width: 100%;

  .ant-table {
    &.ant-table-bordered > .ant-table-container {
      border: none;
    }

    .ant-table-container {
      .ant-table-thead {
        th {
          border-color: var(--greyscale-dark-white) !important;
          border-inline-end: none !important;
          color: var(--greyscale-shade);
          font-size: var(--font-size-small);
          height: ${IMPORT_TABLE_COLUMN_HEADER_HEIGHT}px;
          line-height: var(--font-line-height-regular);

          &.row-column {
            border-inline-end: 1px solid var(--greyscale-dark-white) !important;
          }

          &.invalid-column {
            background-color: var(--greyscale-porcelain);
            color: var(--greyscale-concrete);
          }

          &:first-child {
            padding-left: 10px;
          }
        }
      }

      .ant-table-tbody {
        height: calc(100vh - ${HEIGHT_OF_COMPONENTS_OUTSIDE_IMPORT_TABLE_BODY}px);

        .ant-table-cell {
          border-inline-end: none !important;
          height: 41px;
          line-height: var(--font-line-height-regular);
          padding: 10px;

          &.row-column {
            border-inline-end: 1px solid var(--greyscale-dark-white) !important;
            white-space: nowrap;
          }

          &.status-column {
            display: flex;
          }

          &.invalid-column {
            background-color: var(--greyscale-porcelain);
            color: var(--greyscale-concrete);
          }

          &:first-child {
            padding-left: 10px;
          }
        }

        .ant-table-tbody-virtual-scrollbar {
          &.ant-table-tbody-virtual-scrollbar-horizontal {
            visibility: visible !important;
          }

          .ant-table-tbody-virtual-scrollbar-thumb {
            background: rgb(136 136 136 / 30%) !important;
          }
        }
      }
    }
  }

  .ant-table-wrapper {
    .ant-table.ant-table-empty {
      .ant-table-body {
        height: calc(100vh - ${HEIGHT_OF_COMPONENTS_OUTSIDE_IMPORT_TABLE_BODY}px);
        max-height: calc(100vh - ${HEIGHT_OF_COMPONENTS_OUTSIDE_IMPORT_TABLE_BODY}px) !important;

        .ant-table-tbody {
          height: calc(100vh - ${HEIGHT_OF_COMPONENTS_OUTSIDE_IMPORT_TABLE_BODY_PLUS_SCROLLBAR}px);

          .ant-table-placeholder {
            height: 100%;
          }
        }
      }
    }
  }
`;

const ImportTableCheckboxesWrapper = styled.div`
  height: 45px;
`;

const ImportTableCheckboxesList = styled.div`
  background-color: var(--greyscale-white);
  border-radius: var(--border-radius-small);
  display: flex;
  flex-direction: row;
  overflow: auto;
  padding: 5px;

  .ant-checkbox-wrapper {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    margin-right: 10px;
    white-space: nowrap;

    &.checkbox-invalid-column {
      color: var(--greyscale-concrete);
    }
  }
`;

const ImportTableStatusTag = styled.div`
  align-items: center;
  color: ${(props) => props.$color};
  display: flex;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-small);
  margin-right: 3px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ImportTableStatusCircle = styled.div`
  background-color: ${(props) => props.$color};
  border-radius: 50%;
  display: inline-block;
  height: 15px;
  margin-right: 5px;
  width: 15px;
`;

const ImportTableStatusLabel = styled.span``;

const ImportTableErrorCellWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
`;

const ImportTableErrorCellText = styled.span`
  color: var(--primary-oqio-red);
`;

const ImportTableErrorCellInfoIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  margin-left: 5px;

  svg {
    path {
      fill: var(--primary-oqio-red);
      stroke: var(--primary-oqio-red);
    }

    circle {
      stroke: var(--primary-oqio-red);
    }
  }
`;

const ImportTableErrorTooltipMessageList = styled.div`
  background-color: var(--greyscale-white);
  display: flex;
  flex-direction: column;
`;

const ImportTableErrorTooltipMessage = styled.div`
  border-bottom: 1px solid var(--greyscale-dark-white);
  color: var(--primary-oqio-red);
  font-size: var(--font-size-small);
  line-height: var(--font-line-height-small);
  margin-bottom: 5px;
  padding-bottom: 5px;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const ImportSummaryRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ImportSummaryIcon = styled.div`
  display: flex;
  margin-right: 5px;

  svg {
    path {
      stroke: var(--primary-oqio-blue);
    }
  }
`;

const ImportSummaryText = styled.span`
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
`;

const ImportSummaryHighlightedText = styled.span`
  color: var(--primary-oqio-blue);
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-semi-bold);
  line-height: var(--font-line-height-regular);
  margin-right: 5px;
`;

const ImportSuccessDropzone = styled.div`
  align-items: center;
  background-color: var(--secondary-whisper);
  border: 2px solid var(--primary-oqio-blue);
  border-radius: var(--border-radius-small);
  display: flex;
  flex-direction: column;
  gap: 2px;
  height: 70px;
  justify-content: center;
  padding: 10px;
  text-align: center;
  width: 100%;
`;

const ImportSuccessText = styled.div`
  color: var(--greyscale-blackish);
  display: flex;
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-medium);
  gap: 8px;
  height: 20px;
  line-height: var(--font-line-height-regular);
`;

const ImportSuccessSubText = styled.div`
  color: var(--greyscale-blackish);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
`;

const ImportSuccessSelectLink = styled.button`
  background: none;
  border: none;
  color: var(--primary-oqio-blue);
  cursor: pointer;
  font-size: inherit;
  padding: 0;
  text-decoration: underline;
`;

const ImportSuccessIconWrapper = styled.div`
  svg {
    path {
      stroke: var(--primary-oqio-blue);
    }
  }
`;

const ImportUploaderVisibilityWrapper = styled.div`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
  height: 70px;
`;

const ImportModalFooterActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
`;

const ImportModalFooterPlaceholder = styled.div`
  height: 100%;
  width: 100%;
`;

const ImportModalTitle = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  height: 160px;
  justify-content: center;
  max-width: 660px;

  h2 {
    margin: 0 0 20px;
  }

  p {
    color: var(--greyscale-blackish);
    font-size: var(--font-size-regular);
    font-weight: var(--font-weight-regular);
    line-height: var(--font-line-height-regular);
    margin: 0;
    max-width: none;
  }

  a {
    color: var(--primary-oqio-blue);
    display: inline-block;
    font-size: var(--font-size-regular);
    font-weight: var(--font-weight-medium);
    line-height: var(--font-line-height-regular);
    margin-top: 10px;
    max-width: none;

    &:hover {
      color: var(--primary-oqio-blue-hover);
    }
  }
`;

const ImportModalBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
  padding: 0 20px;
`;

const ImportModalLoadingState = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;

  /* Beide Container (Onboarding-Flow + normaler Stammdaten-Import) teilen sich die Styles. */
  .onboarding-import-loading-state-content,
  .import-loading-state-content {
    display: flex;
    flex: 1 1 auto;
    height: 100%;
    min-height: 0;

    > * {
      display: flex;
      flex: 1 1 auto;
      height: 100%;
      min-height: 0;
    }
  }

  .onboarding-import-loading-indicator,
  .import-loading-indicator {
    display: flex;
    flex: 1 1 auto;
    height: 100%;
    min-height: 0;
  }
`;

const ImportModalTableWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;

  /*
   * Beide Container (Onboarding-Flow + normaler Stammdaten-Import) teilen sich die Styles.
   * Nesting macht die AntD-Overrides lesbarer als wiederholte Selektor-Listen.
   */
  .onboarding-import-table-section,
  .import-table-section {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    width: 100%;

    .ant-table-wrapper,
    .ant-spin-nested-loading,
    .ant-spin-container,
    .ant-table {
      height: 100%;
      min-width: 0;
      width: 100%;
    }

    .ant-table-container {
      height: 100%;
    }

    .ant-table .ant-table-container .ant-table-tbody {
      height: auto !important;
    }

    /* AntDs Empty-State zwingt sonst eine feste Höhe — beide brauchen Auto. */
    .ant-table-wrapper .ant-table.ant-table-empty {
      .ant-table-body {
        height: auto !important;
        max-height: none !important;
      }

      .ant-table-tbody {
        height: auto !important;
      }
    }
  }
`;

export {
  ImportMainWrapper,
  ImportHeaderWrapper,
  ImportSectionWrapper,
  ImportHeaderButtonsWrapper,
  ImportUploaderSectionWrapper,
  ImportLoadingStateWrapper,
  ImportUploaderDropzoneWrapper,
  ImportUploaderTextWrapper,
  ImportUploaderText,
  ImportUploaderHighlightedText,
  ImportTableStatusTag,
  ImportTableSectionWrapper,
  ImportTableWrapper,
  ImportTableStatusCircle,
  ImportTableStatusLabel,
  ImportTableErrorCellWrapper,
  ImportTableErrorCellText,
  ImportTableErrorCellInfoIconWrapper,
  ImportTableErrorTooltipMessageList,
  ImportTableErrorTooltipMessage,
  ImportTableCheckboxesWrapper,
  ImportTableCheckboxesList,
  ImportSummaryRow,
  ImportSummaryIcon,
  ImportSummaryText,
  ImportSummaryHighlightedText,
  ImportSuccessDropzone,
  ImportSuccessText,
  ImportSuccessSubText,
  ImportSuccessSelectLink,
  ImportSuccessIconWrapper,
  ImportUploaderVisibilityWrapper,
  ImportModalFooterActions,
  ImportModalFooterPlaceholder,
  ImportModalTitle,
  ImportModalBody,
  ImportModalLoadingState,
  ImportModalTableWrapper
};
